/**
 * queueTypes.ts — the offline-outbox QUEUE TYPE CONTRACT.
 *
 * Charter: ONE thing — the durable string key every queued write is stored under.
 *
 * This is the only piece of the handler surface that BOTH sides need: producers
 * (the hooks that enqueue / durableSubmit) and consumers (the per-domain handler
 * modules that replay). It lives in its own module so the domain modules can read
 * the contract without importing the registration facade that imports THEM — the
 * dependency direction stays one-way (facade → domain → contract).
 *
 * The key strings are the persisted format of rows already sitting in a rep's
 * IndexedDB outbox. CHANGING ONE ORPHANS EVERY QUEUED ROW THAT CARRIES IT — add a
 * new key and migrate rather than renaming a live one.
 *
 * Re-exported by `./handlers` so every existing `@appic/lib/offline/handlers`
 * import site keeps working unchanged.
 */

export const QUEUE_TYPES = {
  APPOINTMENT_CREATE: 'appointment:create',
  APPOINTMENT_UPDATE: 'appointment:update',
  APPOINTMENT_CANCEL: 'appointment:cancel',
  VISIT_REPORT_SUBMIT: 'visit-report:submit',
  VISIT_REPORT_V2_SUBMIT: 'visit-report-v2:submit',
  VISIT_REPORT_V8_SUBMIT: 'visit-report-v8:submit',
  LEAD_STATUS_UPDATE: 'lead:status-update',
  JOKER_STOP_CREATE: 'joker-stop:create',
  MISSION_DAY_START: 'mission-day:start',
  MISSION_DAY_FINISH: 'mission-day:finish',
  MISSION_DAY_HOME_RETURN: 'mission-day:home-return',
  SCAN_ORDER_CONFIRM: 'scan-order:confirm',
  CALL_RESULT_SUBMIT: 'call-result:submit',
  MISSION_LEAD_REORDER: 'mission-lead:reorder',
  MISSION_DATE_UPDATE: 'mission:date-update',
  REP_PREFERENCES_SAVE: 'rep-preferences:save',
} as const

/**
 * The queue types whose ONLINE submit KEEPS its outbox entry on a FINAL refusal — pinned
 * `failed` at maxRetries, tagged 'non-retryable-4xx' — instead of removing it
 * (durableSubmit; TASK-FE-V8-DOOR-OWNERSHIP-REFUSALS-2026-10-08, AC 9).
 *
 * WHY ONLY THE VISIT-REPORT DOORS: a refused visit report is the rep's field work — the
 * one write whose loss cannot be redone from the office. Pinned, it stays visible in the
 * failed-entries surface with its code, never re-sent by itself (the pin is outside
 * `pending`, and isRetryableReason refuses it), and leaves only by the rep's discard
 * (queueExit). Every other door (scan order, call results, Tagesplanung …) keeps the
 * removal: its form is still on screen, the rep corrects and resends there.
 *
 * An explicit allow-list by design — a door joins it by name, never by inference.
 */
export const PIN_ON_FINAL_QUEUE_TYPES: ReadonlySet<string> = new Set<string>([
  QUEUE_TYPES.VISIT_REPORT_V2_SUBMIT,
  QUEUE_TYPES.VISIT_REPORT_V8_SUBMIT,
])

/**
 * The key two attempts of the SAME pinned-on-final write share — its `source_event_id` (both
 * visit-report doors dedup on it) — or null when nothing relates `payload` to another attempt:
 * every door outside PIN_ON_FINAL_QUEUE_TYPES (it never pins online), and a body without the key.
 * A delivered attempt resolves its pinned twins by this key (queueStoreWrite.removeDeliveredTwins).
 */
export function pinnedTwinKeyOf(queueType: string, payload: unknown): string | null {
  if (!PIN_ON_FINAL_QUEUE_TYPES.has(queueType)) return null
  const key = (payload as { source_event_id?: unknown } | null | undefined)?.source_event_id
  return typeof key === 'string' && key !== '' ? key : null
}
